import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTrigger,
} from "@/components/ui/drawer";
import { LoginRoute, SignUpRoute } from "@/features/authentication/route";
import {
	AboutUsRoute,
	BecomeAPartnerRoute,
	GiveawayDetailsRoute,
	GiveawaysRoute,
	HomeRoute,
	MerchCartRoute,
	MerchRoute,
	WinnersRoute,
} from "@/features/home/route";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import NavigationButton from "../components/navigation-button";
import ProfileButton from "../components/profile-button";
import { useIsMobile } from "../hooks/use-mobile";
import { AuthenticationProvider } from "../providers/authentication";
import { useSiteSettings } from "../providers/site-settings";
import { useCart } from "@/features/merch/providers/cart";
import ArrowRightIcon from "../svgs/arrow-right";
import BurgerMenuIcon from "../svgs/burger-menu";
import CloseIcon from "../svgs/close";
import LogoIcon from "../svgs/logo";

function CartCountBadge({ count }: { count: number }) {
	if (count <= 0) return null;

	return (
		<span
			className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-blue-700 px-2 text-xs font-bold leading-none text-white"
			aria-label={`${count} item${count === 1 ? "" : "s"} in cart`}
		>
			{count > 99 ? "99+" : count}
		</span>
	);
}

function WebNavigationBar() {
	const isAuthenticated = AuthenticationProvider.isAuthenticated;
	const { settings } = useSiteSettings();
	const storeEnabled = settings.storeEnabled;
	const cartEnabled = settings.storeEnabled && settings.cartEnabled;
	const { itemCount } = useCart();
	const location = useLocation();
	const isHomePage = location.pathname === HomeRoute;

	// Match NavigationButton / ProfileButton color logic.
	const heroRoutes = [
		HomeRoute,
		AboutUsRoute,
		WinnersRoute,
		BecomeAPartnerRoute,
		GiveawaysRoute,
	];
	const isStoreIndex =
		location.pathname === MerchRoute || location.pathname === `${MerchRoute}/`;
	const isStoreHero = isStoreIndex && storeEnabled;
	const onWhiteBackground = !(heroRoutes.includes(location.pathname) || isStoreHero);

	const isAboutUsPage = location.pathname === AboutUsRoute;
	const isBecomeAPartnerPage = location.pathname === BecomeAPartnerRoute;
		const isGiveawaysPage = location.pathname === GiveawaysRoute;
		const isGiveawayDetailsPage = location.pathname === GiveawayDetailsRoute;
		const isWinnersPage = location.pathname === WinnersRoute;
		const isMerchCartPage = location.pathname.startsWith(MerchCartRoute);
		const isMerchPage = location.pathname.startsWith(MerchRoute);

		const navigationShouldBeTransparent =
			isHomePage ||
			isAboutUsPage ||
			isBecomeAPartnerPage ||
			isGiveawaysPage ||
			isGiveawayDetailsPage ||
			isWinnersPage ||
			(isMerchPage && !isMerchCartPage && storeEnabled);

	const rootContainerClassName = navigationShouldBeTransparent
		? "absolute flex w-full flex-row items-center justify-between px-20 py-4 z-10"
		: "flex flex-row items-center justify-between px-20 py-4";

	return (
		<div className={rootContainerClassName}>
			<div className="flex flex-row gap-8">
				<NavigationButton url={BecomeAPartnerRoute} title="Partners" />
				<NavigationButton
					url={[GiveawaysRoute, GiveawayDetailsRoute]}
					title="Giveaways"
				/>
				{storeEnabled && <NavigationButton url={MerchRoute} title="Store" />}
				<NavigationButton url={WinnersRoute} title="Winners" />
			</div>

			<Link to={HomeRoute}>
				<LogoIcon />
			</Link>

			<div className="flex flex-row items-center gap-8">
				<NavigationButton url={AboutUsRoute} title="About us" />
				{cartEnabled && (
					<NavigationButton
						url={MerchCartRoute}
						title="Cart"
						right={<CartCountBadge count={itemCount} />}
					/>
				)}
				{isAuthenticated && <ProfileButton />}

				{!isAuthenticated && (
					<NavigationButton url={LoginRoute} title="Log in" bold />
				)}

				{!isAuthenticated && (
					<NavigationButton
						url={SignUpRoute}
						title="Sign Up"
						bold
						right={<ArrowRightIcon white={!onWhiteBackground} />}
					/>
				)}
			</div>
		</div>
	);
}

function MobileNavigationBar() {
	const location = useLocation();
	const { settings } = useSiteSettings();
	const storeEnabled = settings.storeEnabled;
	const cartEnabled = settings.storeEnabled && settings.cartEnabled;
	const { itemCount } = useCart();
	const [open, setOpen] = useState<boolean>(false);

	useEffect(() => {
		setOpen(false);
	}, [location.pathname]);

	const locationPath = location.pathname;
	const isHomePage = locationPath === HomeRoute;
	const isAboutUsPage = locationPath === AboutUsRoute;
	const isBecomeAPartnerPage = locationPath === BecomeAPartnerRoute;
		const isGiveawaysPage = locationPath === GiveawaysRoute;
		const isGiveawayDetailsPage = locationPath === GiveawayDetailsRoute;
		const isWinnersPage = locationPath === WinnersRoute;
		const isMerchCartPage = locationPath.startsWith(MerchCartRoute);
		const isMerchPage = locationPath.startsWith(MerchRoute);

		const navigationShouldBeTransparent =
			isHomePage ||
			isAboutUsPage ||
			isBecomeAPartnerPage ||
			isGiveawaysPage ||
			isGiveawayDetailsPage ||
			isWinnersPage ||
			(isMerchPage && !isMerchCartPage && storeEnabled);

	const rootContainerClassName = navigationShouldBeTransparent
		? "absolute w-full flex flex-row justify-between z-9 px-4 py-1 items-center"
		: "flex flex-row justify-between px-4 py-1 items-center";

	// Match NavigationButton / ProfileButton color logic.
	const heroRoutes = [
		HomeRoute,
		AboutUsRoute,
		WinnersRoute,
		BecomeAPartnerRoute,
		GiveawaysRoute,
	];
	const isStoreIndex =
		locationPath === MerchRoute || locationPath === `${MerchRoute}/`;
	const isStoreHero = isStoreIndex && storeEnabled;
	const onWhiteBackground = !(heroRoutes.includes(locationPath) || isStoreHero);

	return (
		<div className={rootContainerClassName}>
			<Drawer direction={"left"} open={open} onOpenChange={setOpen}>
				<DrawerTrigger>
					<button onClick={() => setOpen(true)}>
						<BurgerMenuIcon white={!onWhiteBackground} />
					</button>
				</DrawerTrigger>
				<DrawerContent className="bg-background-blue">
					<DrawerHeader>
						<div className="flex flex-row items-center justify-between">
							<LogoIcon height={56} width={54} />
							<DrawerClose onClick={() => setOpen(false)}>
								<CloseIcon />
							</DrawerClose>
						</div>

						<div className="mt-10 flex flex-col gap-8">
							<NavigationButton url={BecomeAPartnerRoute} title="Partners" />
							<NavigationButton url={GiveawaysRoute} title="Giveaways" />
							{storeEnabled && <NavigationButton url={MerchRoute} title="Store" />}
							{cartEnabled && (
								<NavigationButton
									url={MerchCartRoute}
									title="Cart"
									right={<CartCountBadge count={itemCount} />}
								/>
							)}
							<NavigationButton url={WinnersRoute} title="Winners" />
							<NavigationButton url={AboutUsRoute} title="About us" />
						</div>
					</DrawerHeader>
				</DrawerContent>
			</Drawer>

			<Link to={HomeRoute}>
				<LogoIcon height={56} width={54} />
			</Link>
			<ProfileButton />
		</div>
	);
}

function NavigationBar() {
	const isMobile = useIsMobile();

	return isMobile ? <MobileNavigationBar /> : <WebNavigationBar />;
}

export default NavigationBar;
